/**
 * Utility functions for handling image embedding in messages
 */

export interface ImageData {
  url: string
  alt?: string
}

/**
 * Embeds images into message text using a special format
 * Format: [IMAGE|url|alt] or [IMAGE|url] for simple format
 */
export function embedImagesInText(text: string, images: ImageData[]): string {
  if (images.length === 0) return text

  const imageEmbeds = images.map(img => {
    const alt = img.alt || ''
    return `[IMAGE|${img.url}|${alt}]`
  }).join('')

  return imageEmbeds + text
}

/**
 * Extracts images from message text
 */
export function extractImagesFromText(text: string): { images: ImageData[], cleanText: string } {
  const imageRegex = /\[IMAGE\|([^|]+)\|([^\]]*)\]/g
  const images: ImageData[] = []
  let match

  while ((match = imageRegex.exec(text)) !== null) {
    const [, url, alt] = match
    images.push({
      url,
      alt: alt || undefined,
    })
  }

  // Remove image embeds from text
  const cleanText = text.replace(imageRegex, '').trim()

  return { images, cleanText }
}

/**
 * Validates if a file is a valid image from mobile phones or common computer formats
 */
export function isValidImageFile(file: File): boolean {
  // Mobile phone image formats (primary focus)
  const mobileTypes = [
    'image/jpeg',     // Most common mobile format
    'image/jpg',      // Alternative JPEG
    'image/png',      // Screenshots, high quality photos
    'image/heic',     // iPhone HEIC format (iOS 11+)
    'image/heif',     // iPhone HEIF format
    'image/webp',     // Android WebP format
  ]
  
  // Common computer formats (for testing only)
  const computerTypes = [
    'image/gif',      // Animated images
  ]
  
  const validTypes = [...mobileTypes, ...computerTypes]
  const maxSize = 10 * 1024 * 1024 // 10MB

  return validTypes.includes(file.type) && file.size <= maxSize
}

/**
 * Gets a user-friendly error message for invalid image files
 */
export function getImageValidationError(file: File): string {
  const maxSize = 10 * 1024 * 1024 // 10MB
  
  if (file.size > maxSize) {
    return `Image is too large. Maximum size is ${Math.round(maxSize / (1024 * 1024))}MB.`
  }
  
  // Check if it's a common unsupported format
  const unsupportedFormats = [
    'image/svg+xml',  // SVG
    'image/psd',      // Photoshop
    'image/raw',      // RAW formats
    'image/cr2',      // Canon RAW
    'image/nef',      // Nikon RAW
  ]
  
  if (unsupportedFormats.includes(file.type)) {
    return 'This image format is not supported. Please use JPEG, PNG, or HEIC from your phone.'
  }
  
  return 'Please select a valid image file (JPEG, PNG, HEIC, or WebP from your phone).'
}

/**
 * Creates a preview URL for an image file
 */
export function createImagePreview(file: File): string {
  return URL.createObjectURL(file)
}

/**
 * Revokes a preview URL to free memory
 */
export function revokeImagePreview(url: string): void {
  URL.revokeObjectURL(url)
}
